import { useCallback, useEffect, useRef, useState } from "react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STALE_KEY = "moringa-stale-min";
const AUTO_KEY = "moringa-auto-refresh";
export const STALE_OPTIONS = ["5", "10", "15", "30", "60"];
const read = (k, d) => { try { return localStorage.getItem(k) || d; } catch { return d; } };
export const getStaleMin = () => read(STALE_KEY, "15");
export const getAutoRefresh = () => read(AUTO_KEY, "on") === "on";
export function saveRefreshSettings({ auto, staleMin }) {
    try {
        if (auto !== undefined) localStorage.setItem(AUTO_KEY, auto ? "on" : "off");
        if (staleMin !== undefined) localStorage.setItem(STALE_KEY, staleMin);
    } catch { /* storage unavailable */ }
}
const staleMs = () => Number(getStaleMin()) * 60000;

function ago(ms) {
    const m = Math.floor(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    return h < 24 ? `${h} h ago` : `${Math.floor(h / 24)} d ago`;
}

export function DataAge({ at }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
    if (!at) return null;
    const stale = now - at > staleMs();
    return (
        <span className={stale ? "text-amber-600 dark:text-amber-400 font-medium" : ""} title={`Updated ${new Date(at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`}
            data-testid="header-data-age" data-stale={stale}>
            updated {ago(now - at)}
        </span>
    );
}

export function usMarketOpen(now = new Date()) {
    const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", weekday: "short", hour: "numeric", minute: "numeric", hourCycle: "h23" })
        .formatToParts(now).map((x) => [x.type, x.value]));
    const mins = Number(p.hour) * 60 + Number(p.minute);
    return !["Sat", "Sun"].includes(p.weekday) && mins >= 570 && mins < 960;
}

export function HeaderRefresh({ at }) {
    const qc = useQueryClient();
    const fetching = useIsFetching();
    const [busy, setBusy] = useState(false);
    const lastAuto = useRef(0);
    const run = useCallback(async (auto = false) => {
        if (busy) return;
        setBusy(true);
        try {
            await api.post("/market/refresh");
            await qc.invalidateQueries();
            if (!auto) toast.success("Data refreshed", { duration: 1500 });
        } catch {
            if (!auto) toast.error("Refresh failed");
        } finally {
            setBusy(false);
        }
    }, [busy, qc]);
    // auto-refresh once data goes stale while the US market is open (at most every 5 min)
    useEffect(() => {
        const check = () => {
            if (!at || !getAutoRefresh() || document.hidden || Date.now() - at <= staleMs() || Date.now() - lastAuto.current < 5 * 60000 || !usMarketOpen()) return;
            lastAuto.current = Date.now();
            run(true);
        };
        check();
        const t = setInterval(check, 30000);
        return () => clearInterval(t);
    }, [at, run]);
    return (
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => run()} disabled={busy} title="Refresh market data (auto-refreshes when stale during market hours)" data-testid="header-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${busy || fetching > 0 ? "animate-spin" : ""}`} />
        </Button>
    );
}
